/**
 * One dispatcher for Godot's indexed property arrays (`item_0/text`, `option_2/name`), keyed
 * `vformat("%s%d/%s", prefix, i, name)` (`property_list_helper.cpp:149`) with the index glued to
 * the prefix. The high end (an index past the live element count) is a bound against a sibling
 * count no property validator sees, so ADR-0032 leaves it to a semantic rule.
 */
import { keyShapeError } from './propertyError.js';
import { accepts } from './v.js';
import { IS_VALID_INT_RE, stringToInt } from '../../godot/string/string.js';
import { declaredLeafResolver } from '../../godot/classdb/indexedKey.js';
import { negativeIndexError } from '../reportedIndices.js';
/**
 * The `is_valid_int` gate, the one cite this file owns since every `PropertyListHelper` family
 * shares it: `_get_property` returns nullptr and the write is dropped, ADR-0032's error tier.
 * Each class passes its own cites as `cite: '…'` literals, which `rangeAdvisoryGrounding.test.ts`
 * scrapes out of source.
 */
const IS_VALID_INT_CITE = 'property_list_helper.cpp:53-55';
/**
 * Build the dispatcher, with `leaves` exposed so `boundGrounding`'s sweep
 * recurses past it: a tag on the dispatcher says nothing about the bounds behind
 * it.
 */
export function indexedFamilyValidator(opts) {
    const { prefix, leaves, unknownCode, describes, negativeIndex } = opts;
    const gatesOnValidInt = opts.indexParse === 'is_valid_int';
    // Hoisted out of the per-call body, which runs once per `<prefix><i>/<leaf>`
    // key: one closure per registered family, not one per call.
    const unknown = (key, line) => keyShapeError(key, line, `Unknown ${describes} property: "${key}"`, unknownCode);
    /**
     * Godot refuses to resolve a negative index under either parse, so `_set` treats the key as
     * unrecognised and the write never lands. The index is the one Godot stores: `to_int` reads
     * `settings/a-1/…` as -1, and the `int` keeps the low 32 bits, so `4294967296` is 0.
     */
    const refuseNegative = negativeIndex
        ? (indexText, key, line) => negativeIndexError(indexText, key, line, negativeIndex.message, negativeIndex.code)
        : (indexText, key, line) => stringToInt(indexText) < 0 ? unknown(key, line) : null;
    /**
     * The leaf a key reaches: {@link declaredLeafResolver} for a hand-rolled `_set`, and the exact
     * leaf for `PropertyListHelper`, whose last-`/` cut (property_list_helper.cpp:47) makes a tail
     * fail `is_valid_int`. hasOwnProperty, so `toString` cannot resolve an inherited function.
     */
    const resolveLeaf = gatesOnValidInt
        ? (leafName) => (Object.prototype.hasOwnProperty.call(leaves, leafName) ? leafName : null)
        : declaredLeafResolver(Object.keys(leaves));
    const validator = accepts((key, value, line) => {
        // The first `/` past the prefix ends the index, and the rest is the leaf: a
        // hand-rolled override nests deeper than `PropertyListHelper`'s one segment
        // (`property_list_helper.cpp:47`), as `TwoBoneIK3D`'s
        // `settings/<i>/end_bone/direction` does.
        const slash = key.indexOf('/', prefix.length);
        if (!key.startsWith(prefix) || slash < 0)
            return unknown(key, line);
        const indexText = key.slice(prefix.length, slash);
        const leafName = key.slice(slash + 1);
        if (leafName === '')
            return unknown(key, line);
        // A class that gates on `is_valid_int` has no index at all for text the regex rejects, the empty
        // text included, so the key is unrecognised before an index is ever read. Under `to_int` an
        // empty index is element 0 (`ustring.cpp:2304-2305`).
        if (gatesOnValidInt && !IS_VALID_INT_RE.test(indexText))
            return unknown(key, line);
        const negative = refuseNegative(indexText, key, line);
        if (negative)
            return negative;
        // A non-negative index resolves to some setting and the write lands, so only the leaf can
        // still be refused.
        const resolved = resolveLeaf(leafName);
        if (resolved === null)
            return unknown(key, line);
        // A leaf whose own path carries an index is a nested family (`ChainIK3D`'s
        // `settings/<i>/joints/<j>/bone`, chain_ik_3d.cpp), matched by its slice's own regex through
        // {@link stringToInt}. Absorbing one needs a sub-path that ends in an index
        // (`SpringBoneSimulator3D`'s `collisions/<j>`) and a `negativeIndex` per index position.
        return leaves[resolved](key, value, line);
    }, opts.accepts ?? describes);
    const cites = [
        // The index gate ({@link IS_VALID_INT_CITE}) refuses a real value, so an
        // `is_valid_int` family is grounded with or without a negative-index branch.
        ...(gatesOnValidInt ? [IS_VALID_INT_CITE] : []),
        ...(negativeIndex ? [negativeIndex.cite] : []),
    ];
    if (cites.length > 0) {
        // Two cites where two guards can fire, the way `patternValidator` names
        // both of TileSet's (`resources/tileset/sourceValidators.ts`).
        validator.grounding = { kind: 'enforced', cite: cites.join(', ') };
    }
    else {
        // A `to_int` class resolves every index text to some element, so nothing
        // above refuses a real value: only an unrecognised key shape or leaf name
        // is rejected. Every magnitude bound lives in the leaves.
        validator.formatOnly = true;
    }
    validator.leaves = Object.values(leaves);
    return validator;
}
/**
 * The registry entry of a declared family: its validator under the key that routes
 * `<prefix><i>/<leaf>` to it. `#` selects the glued-index matcher of
 * `ValidatorRegistry.findOwnValidator`. The prefix and index parse come from the declaration.
 */
export function propertyListFamilyEntry(family, options) {
    const validator = indexedFamilyValidator({
        ...options,
        prefix: family.prefix,
        indexParse: family.indexParse,
    });
    return { [`${family.prefix}#/*`]: validator };
}
//# sourceMappingURL=indexedFamily.js.map